<script>
import AkDialogHeadlessNativeScroll from '@components/general/AkDialogHeadlessNativeScroll.vue';
import AIService from '@services/AIService';
import {Icon} from '@iconify/vue';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';

const DEFAULT_MODEL_ID = 'anthropic/claude-sonnet-4.6';

const PROVIDER_ICONS = {
  openai: 'simple-icons:openai',
  anthropic: 'simple-icons:anthropic',
  google: 'simple-icons:google',
  'meta-llama': 'simple-icons:meta',
  mistralai: 'simple-icons:mistralai',
  perplexity: 'simple-icons:perplexity',
  'x-ai': 'simple-icons:x',
  deepseek: 'simple-icons:deepseek',
  nvidia: 'simple-icons:nvidia',
  qwen: 'simple-icons:qwen',
};

export default {
  name: 'aiReplyDialog',
  components: {AkButtonActionV2, AkDialogHeadlessNativeScroll, Icon, AkButtonValidation},
  emits: ['selectReply'],
  data() {
    return {
      models: [],
      search: '',
      replies: [],
      loadingModels: true,
      loadingReplies: false,
      chosenModel: null,
      replyError: null,
      bottomOfReplies: true,
      review: null,
      mobileStep: 'model',
    };
  },
  computed: {
    canGenerate() {
      return !!this.chosenModel && !this.loadingReplies;
    },
    sortedModels() {
      return [...this.models].sort((a, b) => {
        if (a.id === DEFAULT_MODEL_ID) return -1;
        if (b.id === DEFAULT_MODEL_ID) return 1;
        const aFree = !(parseFloat(a.pricing?.prompt) + parseFloat(a.pricing?.completion));
        const bFree = !(parseFloat(b.pricing?.prompt) + parseFloat(b.pricing?.completion));
        return bFree - aFree;
      });
    },
    filteredModels() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.sortedModels;
      return this.sortedModels.filter(
        (m) => m.name.toLowerCase().includes(q) || this.providerOf(m.id).toLowerCase().includes(q),
      );
    },
  },
  mounted() {
    this.fetchModels();
  },
  methods: {
    show(review) {
      this.review = review;
      this.$refs.dialog.show();
    },
    fetchModels() {
      console.log('--------- [AI Reply Dialog] Fetching OpenRouter models ---------');
      this.loadingModels = true;
      return AIService.fetchModels().then((models) => {
        this.models = models;
        this.chosenModel = this.sortedModels[0].id;
        this.loadingModels = false;
      });
    },
    generateReply() {
      if (!this.review.id || !this.chosenModel) return;

      this.loadingReplies = true;
      this.replyError = null;
      const modelId = this.chosenModel;
      return AIService.generateReplyForReview(this.review.id, modelId)
        .then((data) => {
          const model = this.models.find((m) => m.id === modelId);
          this.replies.push({text: data.reply, modelName: model?.name ?? modelId});
        })
        .catch((e) => {
          this.replyError = e.response?.data?.error ?? 'Une erreur est survenue';
        })
        .finally(() => {
          this.loadingReplies = false;
          this.$refs.generateBtn?.init();
          this.$refs.generateBtnMobile?.init();
          this.$nextTick(() => {
            const el = this.$isMobile() ? this.$refs.repliesContainerMobile : this.$refs.repliesContainer;
            if (el) el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
          });
        });
    },
    providerOf(modelId) {
      return modelId.split('/')[0].replace(/^~+/, '');
    },
    iconFor(modelId) {
      const provider = this.providerOf(modelId);
      return PROVIDER_ICONS[provider] || 'ph:robot-bold';
    },
    onRepliesScroll(e) {
      const el = e.target;
      this.bottomOfReplies = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    },
    modelPrice(model) {
      const avg = ((parseFloat(model.pricing?.prompt) + parseFloat(model.pricing?.completion)) / 2) * 1_000_000;
      if (!avg) return 'Gratuit';
      return '$' + (avg < 0.01 ? avg.toPrecision(2) : avg.toFixed(2)) + '/1M';
    },
    select(text) {
      this.$refs.dialog.cancel();
      this.$emit('selectReply', text);
    },
    chooseModel(modelId) {
      this.chosenModel = modelId;
      if (this.$isMobile()) this.mobileStep = 'reply';
    },
  },
  watch: {
    review(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.replies = [];
        this.bottomOfReplies = true;
        this.mobileStep = 'model';
      }
    },
  },
};
</script>

<template>
  <AkDialogHeadlessNativeScroll
    :show-validate-button="false"
    :show-cancel-button="false"
    dismissable
    ref="dialog"
    title="Suggestion IA de réponse"
    width="920px">
    <!-- Desktop layout -->
    <div class="hidden md:flex gap-1 h-[520px]">
      <!-- Model list -->
      <div class="flex flex-col gap-2 shrink-0 w-2/5 min-h-0">
        <div class="relative shrink-0 mr-3">
          <input
            v-model="search"
            type="text"
            placeholder="Rechercher un modèle..."
            class="w-full rounded-lg border border-grey-200 px-3 py-2 pr-8 text-sm text-grey-900 placeholder-grey-400 outline-none focus:border-grey-300" />
          <button
            v-if="search"
            type="button"
            @click="search = ''"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600">
            <i class="ga-icon ga-supprimer font-bold text-md"></i>
          </button>
        </div>
        <div v-if="loadingModels" class="flex items-center justify-center py-12">
          <span class="text-grey-500 text-sm">Chargement des modèles...</span>
        </div>
        <div v-else class="flex flex-col gap-1 overflow-y-auto min-h-0 pr-3">
          <div v-if="!filteredModels.length" class="flex items-center justify-center py-12">
            <span class="text-grey-400 text-sm">Aucun modèle trouvé</span>
          </div>
          <button
            v-for="model in filteredModels"
            :key="model.id"
            type="button"
            @click="chosenModel = model.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all w-full shrink-0"
            :class="
              chosenModel === model.id
                ? 'border-secondary bg-secondary-50'
                : 'border-grey-200 hover:border-grey-300 hover:bg-grey-50'
            ">
            <div
              class="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
              :class="chosenModel === model.id ? 'bg-secondary-200' : 'bg-grey-100'">
              <Icon :icon="iconFor(model.id)" width="18" height="18" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium text-grey-900 truncate">{{ model.name }}</span>
              <span class="text-xs text-grey-400">{{ providerOf(model.id) }}</span>
            </div>
            <span class="shrink-0 text-xs text-grey-500">{{ modelPrice(model) }}</span>
            <Icon
              v-if="chosenModel === model.id"
              icon="ic:round-check-circle"
              width="20"
              class="text-secondary shrink-0" />
          </button>
        </div>
      </div>

      <!-- Right panel -->
      <div class="flex flex-col grow border-l pl-4">
        <div
          ref="repliesContainer"
          class="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4"
          @scroll="onRepliesScroll">
          <div class="flex flex-col gap-1.5">
            <span class="text-xs text-right text-grey-400 font-medium capitalize">{{ review.name }}</span>
            <div class="rounded-lg p-3 text-sm text-grey-900 bg-grey-50 self-end max-w-[90%] whitespace-pre-line">
              {{ review.comment }}
            </div>
          </div>
          <div v-for="(reply, i) in replies" :key="i" class="flex flex-col gap-1.5">
            <span class="text-xs text-grey-400 font-medium">{{ reply.modelName }}</span>
            <div class="rounded-lg text-sm text-grey-900 bg-white whitespace-pre-line">
              {{ reply.text }}
            </div>
            <AkButtonActionV2
              @click="select(reply.text)"
              label="Utiliser"
              class="w-fit !p-2 !py-1 !text-[13px] font-medium !bg-grey-100 text-primary" />
          </div>
          <div v-if="loadingReplies" class="flex gap-1 items-center px-1 pt-2">
            <span class="typing-dot" />
            <span class="typing-dot" />
            <span class="typing-dot" />
          </div>
          <span v-else-if="replyError" class="text-red text-sm px-1">{{ replyError }}</span>
          <div class="sticky bottom-0 mt-auto py-3 w-full transition-all" :class="{'px-5': !bottomOfReplies}">
            <AkButtonValidation
              ref="generateBtn"
              label="Générer une réponse"
              class="!px-6 font-medium w-full transition-all"
              :class="{'btn-shadow': !bottomOfReplies}"
              label-waiting="Génération en cours..."
              :disabled="!canGenerate"
              :blue="true"
              :little="false"
              @action="generateReply" />
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile layout: two-step (choose model -> generate) -->
    <div class="flex md:hidden flex-col h-[75vh] max-h-[600px]">
      <!-- Step 1: model selection -->
      <div v-if="mobileStep === 'model'" class="flex flex-col gap-2 min-h-0 h-full">
        <div class="relative shrink-0">
          <input
            v-model="search"
            type="text"
            placeholder="Rechercher un modèle..."
            class="w-full rounded-lg border border-grey-200 px-3 py-2 pr-8 text-sm text-grey-900 placeholder-grey-400 outline-none focus:border-grey-300" />
          <button
            v-if="search"
            type="button"
            @click="search = ''"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600">
            <i class="ga-icon ga-supprimer font-bold text-md"></i>
          </button>
        </div>
        <div v-if="loadingModels" class="flex items-center justify-center py-12">
          <span class="text-grey-500 text-sm">Chargement des modèles...</span>
        </div>
        <div v-else class="flex flex-col gap-1 overflow-y-auto min-h-0">
          <div v-if="!filteredModels.length" class="flex items-center justify-center py-12">
            <span class="text-grey-400 text-sm">Aucun modèle trouvé</span>
          </div>
          <button
            v-for="model in filteredModels"
            :key="model.id"
            type="button"
            @click="chooseModel(model.id)"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all w-full shrink-0"
            :class="
              chosenModel === model.id
                ? 'border-secondary bg-secondary-50'
                : 'border-grey-200 hover:border-grey-300 hover:bg-grey-50'
            ">
            <div
              class="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
              :class="chosenModel === model.id ? 'bg-secondary-200' : 'bg-grey-100'">
              <Icon :icon="iconFor(model.id)" width="18" height="18" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium text-grey-900 truncate">{{ model.name }}</span>
              <span class="text-xs text-grey-400">{{ providerOf(model.id) }}</span>
            </div>
            <span class="shrink-0 text-xs text-grey-500">{{ modelPrice(model) }}</span>
            <Icon
              v-if="chosenModel === model.id"
              icon="ic:round-check-circle"
              width="20"
              class="text-secondary shrink-0" />
          </button>
        </div>
      </div>

      <!-- Step 2: generate / replies -->
      <div v-else class="flex flex-col min-h-0 h-full gap-2">
        <button
          type="button"
          @click="mobileStep = 'model'"
          class="flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg border border-grey-200 bg-grey-50 text-left">
          <div class="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-grey-100">
            <Icon :icon="iconFor(chosenModel)" width="16" height="16" />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-grey-900 truncate">
              {{ models.find((m) => m.id === chosenModel)?.name }}
            </span>
          </div>
          <span class="shrink-0 text-xs font-medium text-secondary">Changer</span>
        </button>

        <div
          ref="repliesContainerMobile"
          class="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4"
          @scroll="onRepliesScroll">
          <div class="flex flex-col gap-1.5">
            <span class="text-xs text-right text-grey-400 font-medium capitalize">{{ review.name }}</span>
            <div class="rounded-lg p-3 text-sm text-grey-900 bg-grey-50 self-end max-w-[90%] whitespace-pre-line">
              {{ review.comment }}
            </div>
          </div>
          <div v-for="(reply, i) in replies" :key="i" class="flex flex-col gap-1.5">
            <span class="text-xs text-grey-400 font-medium">{{ reply.modelName }}</span>
            <div class="rounded-lg text-sm text-grey-900 bg-white whitespace-pre-line">
              {{ reply.text }}
            </div>
            <AkButtonActionV2
              @click="select(reply.text)"
              label="Utiliser"
              class="w-fit !p-2 !py-1 !text-[13px] font-medium !bg-grey-100 text-primary" />
          </div>
          <div v-if="loadingReplies" class="flex gap-1 items-center px-1 pt-2">
            <span class="typing-dot" />
            <span class="typing-dot" />
            <span class="typing-dot" />
          </div>
          <span v-else-if="replyError" class="text-red text-sm px-1">{{ replyError }}</span>
          <div class="sticky bottom-0 mt-auto py-3 w-full transition-all" :class="{'px-5': !bottomOfReplies}">
            <AkButtonValidation
              ref="generateBtnMobile"
              label="Générer une réponse"
              class="!px-6 font-medium w-full transition-all"
              :class="{'btn-shadow': !bottomOfReplies}"
              label-waiting="Génération en cours..."
              :disabled="!canGenerate"
              :blue="true"
              :little="false"
              @action="generateReply" />
          </div>
        </div>
      </div>
    </div>
  </AkDialogHeadlessNativeScroll>
</template>

<style scoped>
.typing-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #9ca3af;
  animation: typing-bounce 1.2s infinite ease-in-out;
}
.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.btn-shadow {
  @apply !shadow-lg !shadow-primary-900/30 -translate-y-3;
}
</style>
